import { type RefObject, useCallback, useLayoutEffect, useRef } from 'react';

import { logEntryElementId } from '../../utils';

/** Where an entry sits below the top of the panel's visible area, in px, or `undefined` when it is not drawn. */
const entryOffset = (container: HTMLElement | null, eventId: string): number | undefined => {
  const element = document.getElementById(logEntryElementId(eventId));
  if (!container || !element) return undefined;

  return element.getBoundingClientRect().top - container.getBoundingClientRect().top;
};

/**
 * Keeps the reader where they were when earlier entries land above them.
 *
 * Before the read, the first entry and its offset are noted; once a different entry is first, the panel is
 * scrolled by however far the noted one moved. Not left to the browser's scroll anchoring, which Safari has long
 * lacked, and which does not hold the entries still for a reader at the very top of the panel — the one who
 * clicked "Load earlier".
 *
 * A read that brings in nothing the panel draws first leaves the first entry as it was, so the note waits and does
 * nothing: only a later read can put another entry first, and that one takes a fresh note. The note belongs to the
 * `view` it was taken in, so a reader who then filters or reverses the panel puts another entry first without the
 * note moving them.
 */
export const useKeepReadingPosition = (
  containerRef: RefObject<HTMLElement>,
  firstEventId: string | undefined,
  /** What the panel is showing, in what order; the first entry can change with it and no read at all. */
  view: string,
  loadEarlier: () => Promise<void>
): (() => Promise<void>) => {
  const noteRef = useRef<{ eventId: string; offset: number; view: string } | null>(null);

  const loadEarlierInPlace = useCallback(() => {
    const offset = firstEventId ? entryOffset(containerRef.current, firstEventId) : undefined;
    noteRef.current = firstEventId && offset !== undefined ? { eventId: firstEventId, offset, view } : null;

    return loadEarlier();
  }, [containerRef, firstEventId, view, loadEarlier]);

  // Before paint, so the reader never sees the frame where the entries have landed and the panel has not moved.
  useLayoutEffect(() => {
    const note = noteRef.current;
    const container = containerRef.current;
    if (!note || !container || note.eventId === firstEventId) return;

    noteRef.current = null;
    if (note.view !== view) return;
    const offset = entryOffset(container, note.eventId);
    if (offset !== undefined) container.scrollTop += offset - note.offset;
  }, [containerRef, firstEventId, view]);

  return loadEarlierInPlace;
};
